import { useEffect } from 'react';

import { chatService, type ChatMessage } from '../../services/chatService';
import { eventService } from '../../services/eventService';

import type { ChatState } from './types';

/** Recover missed gates without replacing optimistic messages or newer SSE state. */
export function mergeRecoveredPlanningMessages(
  current: ChatMessage[], recovered: ChatMessage[],
): ChatMessage[] {
  const byId = new Map(current.map((message) => [message.id, message]));

  recovered.forEach((message) => {
    const existing = byId.get(message.id);

    byId.set(message.id, existing ? {
      ...message,
      ...existing,
      phase: existing.phase ?? message.phase,
      buttons: existing.buttons ?? message.buttons,
      allowed_buttons: existing.allowed_buttons ?? message.allowed_buttons,
    } : message);
  });

  return Array.from(byId.values()).sort((a, b) => a.created_at.localeCompare(b.created_at));
}

/** Runtime snapshots do not contain chat action gates; reconcile them separately. */
export function usePlanningMessageRecovery(
  projectId: string | null,
  sessionId: string | null,
  setMessages: ChatState['setMessages'],
): void {
  useEffect(() => {
    if (!projectId || !sessionId) return undefined;
    let disposed = false;
    let requested = 0;
    const recover = async (data: unknown) => {
      const payload = data as { project_id?: string; session_id?: string };

      if (payload.project_id && payload.project_id !== projectId) return;
      if (payload.session_id && payload.session_id !== sessionId) return;
      const request = ++requested;

      try {
        const response = await chatService.getMessages(50, 0, { projectId, sessionId });

        if (disposed || request !== requested) return;
        const messages = response.messages.filter((message) => (
          message.project_id === projectId && (!message.session_id || message.session_id === sessionId)
        ));

        setMessages((current) => mergeRecoveredPlanningMessages(current, messages));
      } catch (error) {
        // Keep the live timeline intact; the next completion/reconnect can retry.
        console.warn('Could not synchronize planning action messages', error);
      }
    };
    const unsubscribeComplete = eventService.subscribe('planning_complete', recover);
    const unsubscribeConnected = eventService.subscribe('connected', recover);

    return () => {
      disposed = true;
      unsubscribeComplete();
      unsubscribeConnected();
    };
  }, [projectId, sessionId, setMessages]);
}
